import { useEffect } from 'react';
import {
  transformFromAlignedBox,
  VisibilityBoxMode,
  VisibilityBoxProps,
} from '@fabric-web/3d-viewer';
import * as state from '@/components/Analysis/state';
import * as customBlockState from '@/components/Analysis/modules/CustomBlocks/state';
import { DEFAULT_VISIBILITY_BOX } from '@/constants';
import { GetAssembliesByTemplateFieldsFiltersForEquipmentsTabQuery } from '@/__generated__/graphql';
import { useAtomValue, useSetAtom } from 'jotai';

type Assemblies =
  | GetAssembliesByTemplateFieldsFiltersForEquipmentsTabQuery['getAssembliesByTemplateFieldsFilters']
  | undefined;

export const useAssemblyVisibilityBox = (assemblies: Assemblies) => {
  const selectedAssemblyId = useAtomValue(state.selectedAssemblyId);
  const setCurrentVisibilityBox = useSetAtom(state.currentVisibilityBox);
  const selectedSpherical = useAtomValue(state.selectedSpherical);
  const selectedCustomBlock = useAtomValue(customBlockState.selectedCustomBlock);

  useEffect(() => {
    if (!selectedSpherical) {
      // Don't override the custom block's visibility box when an assembly is selected
      if (selectedCustomBlock?.paintRegionId) return;

      const assemblyBox =
        selectedAssemblyId && assemblies
          ? assemblies.find((item) => item.id === selectedAssemblyId)?.box
          : undefined;

      if (assemblyBox) {
        const vboxFromAssembly: VisibilityBoxProps = {
          ...transformFromAlignedBox({
            min: [assemblyBox.min.x, assemblyBox.min.y, assemblyBox.min.z],
            max: [assemblyBox.max.x, assemblyBox.max.y, assemblyBox.max.z],
          }),
          mode: VisibilityBoxMode.Enabled,
          step: DEFAULT_VISIBILITY_BOX.step,
        };
        setCurrentVisibilityBox(vboxFromAssembly);
        return;
      }
    }

    // Ensure that if the user has a custom block selected, in the Blocks tab,
    // and they move to the assembly tab, they dont lose their view
    if (!selectedCustomBlock?.paintRegionId) {
      setCurrentVisibilityBox(DEFAULT_VISIBILITY_BOX);
    }
  }, [
    assemblies,
    selectedAssemblyId,
    setCurrentVisibilityBox,
    selectedSpherical,
    selectedCustomBlock?.paintRegionId,
  ]);
};
